import { useState } from 'react'

import { useRouter } from 'next/router'

import { indexes } from '@mab-common/utils'

import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Lozenge, { LozengeVariant } from '~/components/atoms/Lozenge/Lozenge'

import LozengeList from '../LozengeList/LozengeList'

import styles from './ArticlesFilter.module.css'
import RecordTypes = indexes.RecordTypes
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useLocale } from 'next-intl'

import classNames from 'classnames'

import type { ThemeType } from '~/components/serializers'
import { getRouterPath } from '~/utils/getRouterPath'

const Catalogue = dynamic(
  () => import('~/components/blocks/Catalogue/Catalogue')
)
const SlideOutPanel = dynamic(
  () => import('~/components/atoms/SlideOutPanel/SlideOutPanel')
)

export interface ArticlesFilterProps {
  testID?: string
  categoryList: {
    name: string
    slug: string
    isHidden?: boolean
  }[]
  articlesTypeSlug: string | boolean
  articleArtists?: any
  hideArtist?: boolean
  consolidateFilters?: boolean
  isArtistIndex?: boolean
  addSpacerTop?: boolean
  theme?: ThemeType
  testAttr?: string
  noMargins?: boolean
}

export default function ArticlesFilter({
  categoryList,
  articleArtists,
  testID,
  articlesTypeSlug,
  hideArtist = false,
  consolidateFilters = false,
  isArtistIndex = false,
  addSpacerTop = false,
  theme,
  noMargins = false,
  testAttr = 'article-main-filters',
}: ArticlesFilterProps) {
  const router = useRouter()
  const currentFilter = router.query.filter as string
  const currentArtist = router.query.artist as string
  const [showSlideOutArtists, setShowSlideOutArtists] = useState<boolean>(false)
  const [showFilterSlideOut, setShowFilterSlideOut] = useState<boolean>(false)
  const locale = useLocale()

  const getCurrentFilterName = (slug: string) => {
    return categoryList?.find(item => item.slug === slug)?.name || ''
  }

  const slugPrefix = locale !== 'en' ? `/${locale}` : ''

  const consolidateFilterAction = (filter: string) => {
    router.push(
      {
        pathname: getRouterPath(router),
        query: {
          ...router.query,
          filter: filter,
          artist: null,
        },
      },
      undefined,
      { shallow: true }
    )
    setShowFilterSlideOut(false)
  }

  // The content to render inside or outside the Container
  const innerContent = (
    <div className={styles.inner}>
      <LozengeList>
        {articleArtists && !hideArtist && (
          <Lozenge
            title={currentArtist ? currentArtist : 'Artist'}
            testAttr="artist-filter-lozenge"
            onClick={() => {
              if (currentArtist) {
                router.push(
                  {
                    pathname: getRouterPath(router),
                    query: {
                      ...router.query,
                      filter: null,
                      artist: null,
                    },
                  },
                  undefined,
                  { shallow: true }
                )
              } else {
                setShowSlideOutArtists(true)
              }
            }}
            count={currentArtist ? 'x' : ''}
            icon={'Filter'}
          />
        )}
        {consolidateFilters && (
          <Lozenge
            title={
              currentFilter ? getCurrentFilterName(currentFilter) : 'Filter'
            }
            testAttr="filter-lozenge"
            onClick={() => {
              if (currentFilter) {
                router.push(
                  {
                    pathname: getRouterPath(router),
                    query: {
                      ...router.query,
                      filter: null,
                      artist: null,
                    },
                  },
                  undefined,
                  { shallow: true }
                )
              } else {
                setShowFilterSlideOut(true)
              }
            }}
            count={currentFilter ? 'x' : ''}
            icon={'Filter'}
          />
        )}

        {!consolidateFilters &&
          categoryList.map((category, i: number) => (
            <Lozenge
              title={category.name}
              key={`category-lozenge-${i}`}
              variant={LozengeVariant.white}
              testAttr={`category-lozenge`}
              onClick={e => {
                e.preventDefault()
                const filter =
                  currentFilter === category.slug ? null : category.slug
                router.push(
                  {
                    pathname: getRouterPath(router),
                    query: {
                      ...router.query,
                      filter: filter,
                      artist: isArtistIndex ? articlesTypeSlug : null,
                      p: null,
                    },
                  },
                  undefined,
                  { shallow: true }
                )
              }}
              isActive={currentFilter === category.slug}
              count={currentFilter === category.slug ? 'x' : ''}
            />
          ))}
        {router.query?.author && (
          <Lozenge
            title={router.query?.author ? (router.query?.author as string) : ''}
            variant={LozengeVariant.white}
            testAttr="author-lozenge"
            onClick={e => {
              e.preventDefault()
              router.push(
                {
                  pathname: getRouterPath(router),
                  query: {
                    ...router.query,
                    author: null,
                    filter: router.query?.filter,
                    artist: router.query?.artist,
                  },
                },
                undefined,
                { shallow: true }
              )
            }}
            isActive={!!router.query?.author}
            count={'x'}
          />
        )}
      </LozengeList>
    </div>
  )

  return (
    <>
      <div
        className={classNames(styles.base, addSpacerTop && styles.spacerTop, {
          [styles.noMargins]: noMargins,
        })}
        data-testid={testID}
        data-test={testAttr}
      >
        {noMargins ? (
          innerContent
        ) : (
          <Container gutters={gutterTypes.always}>{innerContent}</Container>
        )}
      </div>
      {articleArtists && (
        <SlideOutPanel
          isShowing={showSlideOutArtists}
          closeHandler={() => setShowSlideOutArtists(false)}
          title="Artist Filter"
          testAttr="artist-slideout-panel"
        >
          <div data-test="artist-catalogue-filter">
            <Catalogue
              items={articleArtists}
              orderBySurname={true}
              recordType={RecordTypes.ARTICLES}
              closeParentMenu={() => setShowSlideOutArtists(false)}
              testAttrItems="artist-catalogue-filter-profile"
              customClickHandler={(_id: string, slug: string) => {
                router.push(
                  {
                    pathname: getRouterPath(router),
                    query: { ...router.query, artist: slug, filter: null },
                  },
                  undefined,
                  { shallow: true }
                )
              }}
            />
          </div>
        </SlideOutPanel>
      )}
      {consolidateFilters && (
        <SlideOutPanel
          isShowing={showFilterSlideOut}
          closeHandler={() => setShowFilterSlideOut(false)}
          title="Filter"
          titleSmallPadding
          testAttr="filter-slideout-panel"
        >
          <div className={styles.slideOutFilter} data-test="artist-filter-list">
            {categoryList?.map((item, idx) => {
              return (
                <Link
                  title={'View all'}
                  className={styles.slideOutFilterItem}
                  key={`filter-item-${idx}`}
                  href={`${slugPrefix}/articles?filter=${item.slug}`}
                  data-test={`article-filter-list-item`}
                  onClick={e => {
                    e.preventDefault()
                    consolidateFilterAction(item.slug)
                  }}
                >
                  {item.name}
                </Link>
              )
            })}
          </div>
        </SlideOutPanel>
      )}
    </>
  )
}
